import { memo, useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileSpreadsheet, Loader2, Upload, CheckCircle2, AlertTriangle, XCircle, X, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtP = (p) => (p ? `${p.start.slice(8, 10)}.${p.start.slice(5, 7)} – ${p.end.slice(8, 10)}.${p.end.slice(5, 7)}.${p.end.slice(0, 4)}` : '—');

const FileSlot = memo(function FileSlot({ label, hint, file, onPick, onClear, testId }) {
  const ref = useRef(null);
  return (
    <div className="border rounded-lg p-3 flex items-center gap-3 bg-slate-50" data-testid={`${testId}-slot`}>
      <input ref={ref} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPick(f); }} data-testid={`${testId}-input`} />
      <FileSpreadsheet className={`w-8 h-8 shrink-0 ${file ? 'text-emerald-600' : 'text-slate-300'}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {file ? <div className="text-xs text-slate-700 truncate" data-testid={`${testId}-name`}>{file.name}</div> : <div className="text-xs text-slate-500">{hint}</div>}
      </div>
      {file ? (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClear} data-testid={`${testId}-clear`}><X className="w-4 h-4" /></Button>
      ) : (
        <Button variant="outline" size="sm" onClick={() => ref.current?.click()} data-testid={`${testId}-pick`}><Upload className="w-3.5 h-3.5 mr-1" /> Alege</Button>
      )}
    </div>
  );
});

const ImportDialog = memo(function ImportDialog({ open, onClose, onSaved }) {
  const [activity, setActivity] = useState(null);
  const [level, setLevel] = useState(null);
  const [activityOnly, setActivityOnly] = useState(false);
  const [force, setForce] = useState(false);
  const [manual, setManual] = useState({ start: '', end: '' });
  const [needManual, setNeedManual] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setActivity(null); setLevel(null); setActivityOnly(false); setForce(false); setManual({ start: '', end: '' }); setNeedManual(false); setPreview(null); setError(null); } }, [open]);

  const buildForm = (dryRun) => {
    const fd = new FormData();
    fd.append('activity_file', activity);
    if (level) fd.append('level_file', level);
    if (needManual && manual.start && manual.end) { fd.append('period_start', manual.start); fd.append('period_end', manual.end); }
    fd.append('activity_only', activityOnly);
    fd.append('force', force);
    fd.append('dry_run', dryRun);
    return fd;
  };

  useEffect(() => {
    if (!open || !activity || (!level && !activityOnly)) { setPreview(null); return; }
    if (needManual && (!manual.start || !manual.end)) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      setBusy(true); setError(null);
      try {
        const r = await axios.post(`${API_URL}/motivare/import`, buildForm(true), auth());
        if (!cancelled) { setPreview(r.data); setNeedManual(false); }
      } catch (err) {
        if (cancelled) return;
        const d = err.response?.data?.detail;
        if (d?.code === 'period_required') { setNeedManual(true); setPreview(null); }
        else setError(typeof d === 'string' ? d : d?.message || 'Eroare la validare');
        if (d?.code !== 'period_required') setPreview(null);
      } finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [open, activity, level, activityOnly, manual.start, manual.end]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.post(`${API_URL}/motivare/import`, buildForm(false), auth());
      toast.success(`Raportare salvată: ${fmtP(r.data.activity.period)}`);
      onSaved(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Eroare la salvare');
    } finally { setBusy(false); }
  };

  const canSave = !!preview && !busy && (!preview.needs_force || force);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="motivare-import-dialog">
        <DialogHeader>
          <DialogTitle>Actualizează raportarea</DialogTitle>
          <DialogDescription>Încarcă ambele rapoarte primite în aceeași zi. Se salvează împreună, altfel datele ar fi din zile diferite.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <details className="rounded-lg border border-blue-100 bg-blue-50/60 text-xs" data-testid="import-help">
            <summary className="flex items-center gap-2 cursor-pointer px-3 py-2 font-medium text-blue-800 select-none">
              <HelpCircle className="w-4 h-4" /> Cum încarc corect? (pași)
            </summary>
            <div className="px-3 pb-3 pt-1 text-slate-700 space-y-1.5">
              <p><b>Regula de aur:</b> luna o dă <b>raportul de cantitate</b> (data lui de sfârșit). Level-ul se salvează pe aceeași lună.</p>
              <p><b>Închizi luna</b> (ex. Septembrie): cantitate <b>01–30.09</b> + Level-ul complet sosit pe <b>01.10</b> (măsoară 30.08–29.09). → nivelul final de Septembrie.</p>
              <p><b>Începi luna nouă</b> (Octombrie): cantitate <b>01.10–01.10</b> + Level-ul sosit pe <b>02.10</b> (măsoară 29.09–30.09).</p>
              <p><b>Perechea</b> = raportul de cantitate + Level-ul primit în acea zi. Dacă într-o zi <b>nu vine Level</b>, bifează „Doar activitate" — se păstrează ultimul nivel cunoscut.</p>
              <p>Ca să ștergi/reîncarci: butonul <b>„Gestionează încărcările"</b> (ștergi un raport, sau tot de la o dată încolo).</p>
            </div>
          </details>
          <FileSlot label="1. Raport activitate" hint="Numele fișierului trebuie să conțină perioada, ex. 01-24.09.xlsx" file={activity} onPick={setActivity} onClear={() => { setActivity(null); setNeedManual(false); }} testId="import-activity" />
          {needManual && (
            <div className="flex items-center gap-2 text-xs pl-1" data-testid="import-manual-period">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
              <span className="text-slate-600">Perioada nu s-a recunoscut din nume:</span>
              <Input type="date" className="h-8 w-36" value={manual.start} onChange={(e) => setManual({ ...manual, start: e.target.value })} data-testid="import-period-start" />
              <span>–</span>
              <Input type="date" className="h-8 w-36" value={manual.end} onChange={(e) => setManual({ ...manual, end: e.target.value })} data-testid="import-period-end" />
            </div>
          )}
          <FileSlot label="2. Raport Level" hint="Raport_Level_Curieri_… primit pe email" file={level} onPick={(f) => { setLevel(f); setActivityOnly(false); }} onClear={() => setLevel(null)} testId="import-level" />
          {!level && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pl-1">
              <Checkbox checked={activityOnly} onCheckedChange={(v) => setActivityOnly(!!v)} data-testid="import-activity-only" />
              Doar activitate (nu am primit raportul Level) — se păstrează Level-ul anterior
            </label>
          )}

          {busy && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Se verifică fișierele...</div>}
          {error && <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3" data-testid="import-error"><XCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}</div>}

          {preview && (
            <div className="rounded-lg border p-3 text-sm space-y-2" data-testid="import-preview">
              <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div><b>Activitate {fmtP(preview.activity.period)}</b> · {preview.activity.agents_total} curieri{preview.activity.agents_new ? `, ${preview.activity.agents_new} noi` : ''}{preview.activity.columns_new ? `, ${preview.activity.columns_new} coloane noi` : ''}</div>
              </div>
              {preview.level && (
                <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div><b>Level {fmtP(preview.level.period)}</b> · {preview.level.matched} potriviți{preview.level.ignored ? `, ${preview.level.ignored} ignorați (nu sunt curieri ai noștri)` : ''} · <span className={preview.level.awb_total ? '' : 'text-amber-600 font-medium'} data-testid="import-awb-total">{preview.level.awb_total || 0} AWB-uri întârziate</span></div>
                </div>
              )}
              {preview.warnings.map((w, i) => <div key={i} className="flex items-start gap-2 text-amber-700"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {w}</div>)}
              {preview.errors.map((e, i) => <div key={i} className="flex items-start gap-2 text-red-700" data-testid="import-older-warning"><XCircle className="w-4 h-4 mt-0.5 shrink-0" /> {e}</div>)}
              {preview.needs_force && (
                <label className="flex items-center gap-2 text-xs text-red-700 cursor-pointer pt-1">
                  <Checkbox checked={force} onCheckedChange={(v) => setForce(!!v)} data-testid="import-force" /> Înțeleg, suprascrie oricum cu aceste fișiere
                </label>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="import-cancel">Anulează</Button>
          <Button onClick={save} disabled={!canSave} data-testid="import-save">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvează raportarea</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default ImportDialog;
